import React, { useContext, useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import InlineSpinner from "./InlineSpinner";
import { AppContext } from "../context/app.context";
import { useDashboardOpenings } from "../hooks/useDashboardOpenings";
import { useDashboardRegions } from "../hooks/useDashboardRegions";
import {
  buildDashboardOpenings,
  DASHBOARD_SURFACE_FILTER_VALUES,
  DASHBOARD_SURFACE_FILTER_DEFAULT,
} from "../utility/buildDashboardOpenings";
import { getNextBusinessDayDateString } from "../utility/nextBusinessDay";
import {
  getRegionSelectionState,
  toggleRegionInSelection,
  getAllRegionsSelectionState,
  toggleAllRegionsInSelection,
  REGION_STATE_ALL,
  REGION_STATE_SOME,
} from "../utility/dashboardRegionSelection";
import { timeAgoLabel, timeAgoTickInterval } from "../utility/timeAgoLabel";

/**
 * #139 Next Day Openings Dashboard — modal.
 *
 * Phase 8.2 wiring:
 *   - Multi-select location filter (collapsible) with Check-all/Uncheck-all
 *   - Segmented surface-type filter (Soft / Hard / Both)
 *   - Per-surface count breakdown in each location header when filter is "both"
 *   - Expand all / Collapse all toggle for the location groups
 *
 * Filter state lifecycle (changed 2026-05-26 — Leland feedback after #161):
 *
 *   Filter state is SESSION-ONLY — initialized fresh on every modal open.
 *   Defaults: locations = [current Optimizer location only], surface filter
 *   = "both" (no filter). User changes during the session apply immediately
 *   to the in-modal UI but do NOT persist; closing + reopening resets.
 *
 *   Rationale: persisting across opens was producing oddities (stale
 *   location selections, surprising surface filters carried over from
 *   sessions days ago). Resetting on every open is simpler, predictable,
 *   and matches "the dashboard tells me what's open tomorrow at my
 *   location right now."
 *
 *   The `Users.dashboardLocations` and `Users.dashboardSurfaceFilter`
 *   fields are no longer read or written by this modal; they remain in
 *   MongoDB as harmless legacy data. `Users.dashboardAccess` (boolean,
 *   admin-flipped via Compass) is unchanged — that's a gate, not a filter.
 */
function DashboardModal({ setOpenDashboardModal }) {
  const { locationFilter, companyCode, locationArray } = useContext(AppContext);

  // - - - - - session-only filter state (resets on every modal open)
  //
  // `useState` initializer runs once at mount. Since this component
  // unmounts on close and remounts on open (createPortal pattern), each
  // open gets a fresh initial value — exactly what we want.

  const [selectedLocations, setSelectedLocations] = useState(() =>
    locationFilter ? [locationFilter] : []
  );

  const [surfaceFilter, setSurfaceFilterState] = useState(
    DASHBOARD_SURFACE_FILTER_DEFAULT
  );

  const tomorrowDateString = useMemo(() => getNextBusinessDayDateString(), []);

  const {
    dataByLocation,
    isLoading,
    isFetching,
    isError,
    isReady,
    allSettled,
    dataUpdatedAt,
    refetchAll,
  } = useDashboardOpenings({
    companyCode,
    selectedLocations,
    enabled: true,
  });

  // "Updated: X ago" ticker — re-renders the header label on a tiered
  // schedule (every 5 sec while < 1 min, every 30 sec while < 1 hr, etc.)
  // so the label updates even while the user is leaving the modal open.
  const [tickerNow, setTickerNow] = useState(() => Date.now());
  useEffect(() => {
    const delay = timeAgoTickInterval(dataUpdatedAt, tickerNow);
    const id = setInterval(() => setTickerNow(Date.now()), delay);
    return () => clearInterval(id);
  }, [dataUpdatedAt, tickerNow]);
  const updatedAgo = useMemo(
    () => timeAgoLabel(dataUpdatedAt, tickerNow),
    [dataUpdatedAt, tickerNow]
  );

  const { locations, totalOpenings, degradedCount } = useMemo(
    () =>
      buildDashboardOpenings({
        dataByLocation,
        tomorrowDateString,
        surfaceFilter,
      }),
    [dataByLocation, tomorrowDateString, surfaceFilter]
  );

  // #168 — the total is only authoritative when every selected location
  // loaded cleanly. While loading/refreshing, or when one or more locations
  // came back errored / silently-empty (degradedCount > 0), the number is
  // PARTIAL and must be shown as such — otherwise an under/over-count reads
  // as final. (degradedCount catches the silent-empty projects case that
  // never trips isError.)
  // `allSettled` (not isReady) is the authoritative "every selected location
  // is fully done" signal — isReady can flicker true in the gaps between
  // throttle waves. The number is partial until all locations have settled
  // AND none came back degraded.
  const totalIsPartial = !allSettled || degradedCount > 0;

  const availableLocations = useMemo(
    () => (Array.isArray(locationArray) ? locationArray.slice().sort() : []),
    [locationArray]
  );

  // - - - - - region data (Phase 8.3)
  // Fires getOfficeMetaDataByLocation in parallel for every accessible
  // location; resolves to a sorted regionList. During the bulk-push
  // window, offices without a region field are simply skipped — the
  // dashboard still works on individual location checkboxes.
  const { regionList } = useDashboardRegions({
    locations: availableLocations,
    enabled: true,
  });

  // - - - - - location-filter handlers (session-only — no persistence)

  const toggleLocation = (locationCode) => {
    if (!locationCode) return;
    setSelectedLocations((prev) => {
      const isPresent = prev.includes(locationCode);
      return isPresent
        ? prev.filter((l) => l !== locationCode)
        : [...prev, locationCode];
    });
  };

  const toggleAllLocations = () => {
    setSelectedLocations((prev) => {
      const allChecked =
        availableLocations.length > 0 &&
        availableLocations.every((loc) => prev.includes(loc));
      return allChecked ? [] : availableLocations.slice();
    });
  };

  // - - - - - region handlers
  // Region clicks are shortcuts that mutate selectedLocations. Pill
  // visual state is derived from selectedLocations vs each region's
  // locations.

  const toggleRegion = (regionLocations) => {
    setSelectedLocations((prev) =>
      toggleRegionInSelection(regionLocations, prev)
    );
  };

  const toggleAllRegions = () => {
    setSelectedLocations((prev) =>
      toggleAllRegionsInSelection(regionList, prev)
    );
  };

  const allRegionsState = useMemo(
    () => getAllRegionsSelectionState(regionList, selectedLocations),
    [regionList, selectedLocations]
  );

  // - - - - - surface-filter handler (session-only)

  const setSurfaceFilter = (next) => {
    if (!DASHBOARD_SURFACE_FILTER_VALUES.includes(next)) return;
    if (next === surfaceFilter) return;
    setSurfaceFilterState(next);
  };

  // - - - - - filter section collapse

  const [filterCollapsed, setFilterCollapsed] = useState(false);

  const selectedSet = useMemo(
    () => new Set(selectedLocations),
    [selectedLocations]
  );
  const allLocationsChecked =
    availableLocations.length > 0 &&
    availableLocations.every((loc) => selectedSet.has(loc));

  // - - - - - location-group collapse (lifted to parent so the
  // expand-all/collapse-all toggle can hit them all at once)

  const [collapsedLocations, setCollapsedLocations] = useState({}); // { [locationCode]: true }

  const expandableLocationCodes = useMemo(
    () =>
      locations.filter((l) => l.openings.length > 0).map((l) => l.locationCode),
    [locations]
  );

  const allLocationsCollapsed =
    expandableLocationCodes.length > 0 &&
    expandableLocationCodes.every((code) => collapsedLocations[code]);

  const toggleLocationGroup = (locationCode) => {
    setCollapsedLocations((prev) => ({
      ...prev,
      [locationCode]: !prev[locationCode],
    }));
  };

  const toggleAllLocationGroups = () => {
    if (allLocationsCollapsed) {
      // Expand all: clear all collapsed flags
      setCollapsedLocations({});
    } else {
      // Collapse all expandable: set true for each
      const next = {};
      for (const code of expandableLocationCodes) next[code] = true;
      setCollapsedLocations(next);
    }
  };

  return (
    <Modal onClose={() => setOpenDashboardModal({ show: false })}>
      <Modal.Header>
        <span className="inline-flex items-baseline gap-3 w-full">
          <span>Next Day Openings</span>
          <span className="text-base opacity-75">
            ({tomorrowDateString})
          </span>
          {updatedAgo && (
            <span className="text-sm opacity-60">
              · Updated: {updatedAgo}
            </span>
          )}
          <button
            type="button"
            onClick={() => refetchAll && refetchAll()}
            disabled={isFetching}
            title="Refresh all dashboard data"
            aria-label="Refresh dashboard data"
            className="ml-auto text-sm px-2 py-0.5 rounded hover:opacity-75 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              border: "1px solid rgba(255,255,255,0.25)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            {isFetching ? (
              <span className="inline-flex items-center gap-1.5">
                <InlineSpinner size="0.9em" ariaLabel="refreshing" />
                <span>Refreshing</span>
              </span>
            ) : (
              "↻ Refresh"
            )}
          </button>
        </span>
      </Modal.Header>
      <Modal.Content>
        <div className="p-6 max-w-3xl">
          {/* - - - status line - - - */}
          <div className="mb-3 text-sm opacity-75">
            Company: <span className="font-bold">{companyCode || "(none)"}</span>{" "}
            ·{" "}
            <span>
              Locations:{" "}
              <span className="font-bold">{selectedLocations.length}</span>
            </span>{" "}
            ·{" "}
            <span>
              Total openings:{" "}
              <span className="font-bold">
                {totalIsPartial ? `~${totalOpenings}` : totalOpenings}
              </span>
              {totalIsPartial && (
                <span
                  className="ml-1 text-xs"
                  style={{ color: "#FDB824", fontWeight: "bold" }}
                  title="The total is still settling or some locations failed to load — treat it as partial."
                >
                  (partial)
                </span>
              )}
            </span>{" "}
            ·{" "}
            {degradedCount > 0 && (
              <>
                <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                  {degradedCount} location{degradedCount === 1 ? "" : "s"} failed
                  to load
                </span>{" "}
                ·{" "}
              </>
            )}
            <span>
              Status:{" "}
              {isError ? (
                <span style={{ color: "#dc2626", fontWeight: "bold" }}>
                  some queries failed
                </span>
              ) : isLoading ? (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: "#FDB824", fontWeight: "bold" }}
                >
                  <InlineSpinner size="0.9em" ariaLabel="loading" />
                  <span>loading</span>
                </span>
              ) : isFetching ? (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: "#FDB824", fontWeight: "bold" }}
                >
                  <InlineSpinner size="0.9em" ariaLabel="refreshing" />
                  <span>refreshing</span>
                </span>
              ) : isReady && degradedCount > 0 ? (
                <span style={{ color: "#FDB824", fontWeight: "bold" }}>
                  partial — retry recommended
                </span>
              ) : isReady ? (
                <span style={{ color: "#22c55e", fontWeight: "bold" }}>
                  ready
                </span>
              ) : (
                <span className="opacity-60">no locations to fetch</span>
              )}
            </span>
          </div>

          {/* - - - filter section (collapsible) - - - */}
          {availableLocations.length === 0 ? null : (
            <div className="mb-4 p-3 border-l-2 border-yellow bg-skin-base bg-opacity-50">
              <div className="flex items-baseline gap-3 mb-1">
                <button
                  type="button"
                  onClick={() => setFilterCollapsed((c) => !c)}
                  className="text-xs font-semibold flex items-baseline gap-2 hover:opacity-75 cursor-pointer"
                  aria-expanded={!filterCollapsed}
                  aria-controls="dashboard-filter-body"
                >
                  <span
                    aria-hidden="true"
                    style={{ width: "0.75rem", textAlign: "center" }}
                  >
                    {filterCollapsed ? "▶" : "▼"}
                  </span>
                  Filter
                  <span className="opacity-60 font-normal">
                    ({selectedLocations.length} of {availableLocations.length}{" "}
                    locations · {labelForSurface(surfaceFilter)})
                  </span>
                </button>
                {!filterCollapsed && (
                  <button
                    type="button"
                    onClick={toggleAllLocations}
                    className="ml-auto text-xs px-2 py-0.5 rounded hover:opacity-75 cursor-pointer"
                    style={{
                      border: "1px solid rgba(255,255,255,0.25)",
                      backgroundColor: "rgba(255,255,255,0.05)",
                    }}
                  >
                    {allLocationsChecked ? "Uncheck all" : "Check all"}
                  </button>
                )}
              </div>
              {!filterCollapsed && (
                <div id="dashboard-filter-body">
                  {/* Surface filter (segmented control) */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs opacity-75 mr-1">Surface:</span>
                    <div
                      role="radiogroup"
                      aria-label="Surface filter"
                      className="flex gap-1"
                    >
                      {DASHBOARD_SURFACE_FILTER_VALUES.map((v) => {
                        const active = surfaceFilter === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setSurfaceFilter(v)}
                            className={`text-xs px-2 py-1 rounded cursor-pointer hover:opacity-75 ${
                              active ? "font-bold" : "opacity-60"
                            }`}
                            style={{
                              backgroundColor: active
                                ? "rgba(253, 184, 36, 0.22)"
                                : "transparent",
                              border: `1px solid ${
                                active ? "#FDB824" : "rgba(255,255,255,0.15)"
                              }`,
                              minWidth: "3.5rem",
                            }}
                          >
                            {labelForSurface(v)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Region pills (Phase 8.3) — shortcut for toggling
                      groups of locations. Hidden until at least one
                      office has a region field populated. */}
                  {regionList.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-xs opacity-75">Regions:</span>
                        <span className="text-xs opacity-50">
                          (shortcut — toggles each region's locations on/off)
                        </span>
                        <button
                          type="button"
                          onClick={toggleAllRegions}
                          className="ml-auto text-xs px-2 py-0.5 rounded hover:opacity-75 cursor-pointer"
                          style={{
                            border: "1px solid rgba(255,255,255,0.25)",
                            backgroundColor: "rgba(255,255,255,0.05)",
                          }}
                        >
                          {allRegionsState === REGION_STATE_ALL
                            ? "Uncheck all regions"
                            : "Check all regions"}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {regionList.map((entry) => {
                          const state = getRegionSelectionState(
                            entry.locations,
                            selectedLocations
                          );
                          const isAll = state === REGION_STATE_ALL;
                          const isSome = state === REGION_STATE_SOME;
                          // Visual: filled yellow when "all", dashed
                          // yellow border + light tint when "some",
                          // transparent when "none".
                          return (
                            <button
                              key={entry.region}
                              type="button"
                              onClick={() => toggleRegion(entry.locations)}
                              className={`flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer select-none hover:opacity-75 ${
                                isAll
                                  ? "font-bold"
                                  : isSome
                                  ? "font-medium"
                                  : "opacity-60"
                              }`}
                              style={{
                                backgroundColor: isAll
                                  ? "rgba(253, 184, 36, 0.22)"
                                  : isSome
                                  ? "rgba(253, 184, 36, 0.08)"
                                  : "transparent",
                                border: `1px ${
                                  isSome ? "dashed" : "solid"
                                } ${
                                  isAll || isSome
                                    ? "#FDB824"
                                    : "rgba(255,255,255,0.15)"
                                }`,
                              }}
                              title={`${entry.region}: ${entry.locations.length} location${
                                entry.locations.length === 1 ? "" : "s"
                              } (${entry.locations.join(", ")})`}
                              aria-pressed={isAll}
                            >
                              <span aria-hidden="true">
                                {isAll ? "☑" : isSome ? "◐" : "☐"}
                              </span>
                              {entry.region}
                              <span className="opacity-60 font-normal">
                                ({entry.locations.length})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Location pills */}
                  <div className="text-xs opacity-75 mb-1">Locations:</div>
                  <div className="flex flex-wrap gap-2">
                    {availableLocations.map((loc) => {
                      const checked = selectedSet.has(loc);
                      return (
                        <label
                          key={loc}
                          className={`flex items-center gap-1 px-2 py-1 text-xs rounded cursor-pointer select-none ${
                            checked ? "font-bold" : "opacity-60"
                          }`}
                          style={{
                            backgroundColor: checked
                              ? "rgba(253, 184, 36, 0.18)"
                              : "transparent",
                            border: `1px solid ${
                              checked ? "#FDB824" : "rgba(255,255,255,0.15)"
                            }`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLocation(loc)}
                            className="cursor-pointer"
                          />
                          {loc}
                        </label>
                      );
                    })}
                  </div>
                  {selectedLocations.length === 0 && (
                    <div className="mt-2 text-xs italic opacity-50">
                      Nothing selected. Check any location above to load
                      tomorrow's openings.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* - - - expand/collapse-all toggle - - - */}
          {expandableLocationCodes.length > 1 && (
            <div className="flex items-baseline mb-2">
              <button
                type="button"
                onClick={toggleAllLocationGroups}
                className="ml-auto text-xs px-2 py-0.5 rounded hover:opacity-75 cursor-pointer"
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  backgroundColor: "rgba(255,255,255,0.05)",
                }}
              >
                {allLocationsCollapsed ? "Expand all" : "Collapse all"}
              </button>
            </div>
          )}

          {/* - - - per-location openings groups - - - */}
          {selectedLocations.length === 0 ? (
            <div className="border-l-2 border-yellow pl-4 text-sm opacity-75">
              <em>
                No locations selected. Pick at least one location above to see
                openings.
              </em>
            </div>
          ) : (
            <div>
              {locations.map((loc) => (
                <LocationGroup
                  key={loc.locationCode}
                  loc={loc}
                  surfaceFilter={surfaceFilter}
                  collapsed={!!collapsedLocations[loc.locationCode]}
                  onToggle={() => toggleLocationGroup(loc.locationCode)}
                />
              ))}
            </div>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}

// Display label for the three surface-filter values. Kept module-local so
// the modal + tests use the same casing.
function labelForSurface(v) {
  if (v === "soft") return "Soft";
  if (v === "hard") return "Hard";
  return "Both";
}

// - - - - - - - - - -
// Per-location group: clickable header (location + counts + status +
// expand/collapse arrow) followed by 4-column table of installer rows
// when expanded. Collapse state is lifted to the parent so the
// Expand-all/Collapse-all toggle can hit them in one shot.

const ROW_GRID_TEMPLATE_COLUMNS = "6rem 1fr 8rem 1fr";

function LocationGroup({ loc, surfaceFilter, collapsed, onToggle }) {
  const {
    locationCode,
    openings,
    isLoading,
    isFetching,
    isError,
    candidatesCount,
    softCandidatesCount,
    hardCandidatesCount,
    softOpeningsCount,
    hardOpeningsCount,
  } = loc;

  // Status badge priority (highest → lowest):
  //   1. error    — red, never overridden by other states
  //   2. loading  — initial fetch, no data yet
  //   3. refreshing — refetch in flight with existing (stale) data showing
  //   4. no openings — settled state, empty result
  //   5. nothing  — settled state with openings rendered below
  let statusBadge = null;
  if (isError) {
    statusBadge = (
      <span className="ml-3 text-xs font-bold" style={{ color: "#dc2626" }}>
        error fetching
      </span>
    );
  } else if (isLoading) {
    statusBadge = (
      <span
        className="ml-3 text-xs font-bold inline-flex items-center gap-1"
        style={{ color: "#FDB824" }}
      >
        <InlineSpinner size="0.85em" ariaLabel="loading" />
        <span>loading</span>
      </span>
    );
  } else if (isFetching) {
    statusBadge = (
      <span
        className="ml-3 text-xs font-bold inline-flex items-center gap-1"
        style={{ color: "#FDB824" }}
      >
        <InlineSpinner size="0.85em" ariaLabel="refreshing" />
        <span>refreshing</span>
      </span>
    );
  } else if (openings.length === 0) {
    statusBadge = (
      <span className="ml-3 text-xs opacity-50">no openings</span>
    );
  }

  const hasOpenings = openings.length > 0;
  const showRows = !collapsed && hasOpenings;

  // Build the count summary. When filter is "both" AND both surfaces
  // have non-zero counts, split into "X soft, Y hard". Otherwise show
  // the flat count.
  const showBothBreakdown =
    surfaceFilter === "both" &&
    (softOpeningsCount + hardOpeningsCount > 0 ||
      softCandidatesCount + hardCandidatesCount > 0);

  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={() => hasOpenings && onToggle && onToggle()}
        disabled={!hasOpenings}
        className={`w-full text-left flex items-baseline border-b border-yellow pb-1 mb-2 ${
          hasOpenings ? "hover:opacity-75 cursor-pointer" : "cursor-default"
        }`}
        aria-expanded={hasOpenings ? !collapsed : undefined}
        aria-controls={`dashboard-location-rows-${locationCode}`}
      >
        <span
          className="text-xs mr-2 inline-block"
          style={{ width: "1rem", textAlign: "center" }}
          aria-hidden="true"
        >
          {hasOpenings ? (collapsed ? "▶" : "▼") : "·"}
        </span>
        <span className="text-lg font-bold">{locationCode}</span>
        <span className="ml-3 text-sm opacity-75">
          {openings.length} opening{openings.length === 1 ? "" : "s"}
          {showBothBreakdown && (openings.length > 0) && (
            <span className="opacity-60 font-normal">
              {" "}
              ({softOpeningsCount} soft, {hardOpeningsCount} hard)
            </span>
          )}
        </span>
        <span className="ml-2 text-xs opacity-50">
          / {candidatesCount} installer{candidatesCount === 1 ? "" : "s"}
          {showBothBreakdown && (candidatesCount > 0) && (
            <span>
              {" "}
              ({softCandidatesCount} soft, {hardCandidatesCount} hard)
            </span>
          )}
        </span>
        {statusBadge}
      </button>
      {showRows && (
        <div id={`dashboard-location-rows-${locationCode}`} className="ml-6">
          {/* Header row */}
          <div
            className="text-xs opacity-60 pb-1 mb-1 border-b border-skin-base"
            style={{
              display: "grid",
              gridTemplateColumns: ROW_GRID_TEMPLATE_COLUMNS,
              gap: "0.75rem",
            }}
          >
            <span>Payroll #</span>
            <span>Installer</span>
            <span>Surface</span>
            <span>Cell content</span>
          </div>
          {/* Data rows */}
          {openings.map((inst) => (
            <div
              key={inst.payrollNo}
              data-testid="dashboard-opening-row"
              className="py-1 text-sm items-baseline"
              style={{
                display: "grid",
                gridTemplateColumns: ROW_GRID_TEMPLATE_COLUMNS,
                gap: "0.75rem",
              }}
            >
              <span className="font-mono opacity-75">{inst.payrollNo}</span>
              <span className="font-medium">{inst.name}</span>
              <span className="text-xs opacity-50 self-center">
                {inst.schedulingSurface || ""}
              </span>
              <span
                className="text-xs"
                style={{
                  opacity: inst.cellContent ? 0.85 : 0.35,
                  fontStyle: inst.cellContent ? "normal" : "italic",
                }}
              >
                {inst.cellContent || "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DashboardModal;
